import { forwardRef, useState, type CSSProperties, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { cx } from "./fieldContext";
export type { TagTone } from "./tagTone";

import { resolveTagTone } from "./tagTone";
import { KEY_ENTER, KEY_SPACE } from "./keyboardKeys";

export interface TagProps extends Omit<HTMLAttributes<HTMLSpanElement>, "color"> {
  children?: ReactNode;
  /** Ton des Kits, Antd-Farb-/Statusname oder eine freie Farbe (z. B. Kategoriefarbe aus den Stammdaten). */
  color?: string;
  icon?: ReactNode;
  closable?: boolean;
  closeIcon?: ReactNode;
  onClose?: (event: MouseEvent<HTMLElement>) => void;
  bordered?: boolean;
  variant?: "filled" | "solid" | "outlined";
  style?: CSSProperties;
}

/** Status-Etikett des Kits (Ersatz für Antd `Tag`). */
export const TagBase = forwardRef<HTMLSpanElement, TagProps>(function Tag({ children, color, icon, closable, closeIcon, onClose, bordered, variant, className, style, ...rest }, ref) {
  const [closed, setClosed] = useState(false);
  if (closed) return null;
  const tone = resolveTagTone(color);
  // Freie Farbe (z. B. die Kategoriefarbe aus den Stammdaten): als Akzent setzen, Fläche daraus abgeleitet.
  const customStyle = tone ? style : ({ "--mq-kit-tag-color": color, ...style } as CSSProperties);
  return (
    <span
      ref={ref}
      className={cx("ck-tag", variant === "solid" && "ck-tag--solid", (bordered === false || variant === "filled") && "ck-tag--borderless", className)}
      data-tone={tone ?? "custom"}
      style={customStyle}
      {...rest}
    >
      {icon ? <span className="ck-tag-icon" aria-hidden="true">{icon}</span> : null}
      {children}
      {closable ? (
        <button
          type="button"
          className="ck-tag-close"
          onClick={(event) => {
            event.stopPropagation();
            onClose?.(event);
            if (!event.defaultPrevented) setClosed(true);
          }}
        >
          {closeIcon ?? <CloseOutlined />}
        </button>
      ) : null}
    </span>
  );
});

export interface CheckableTagProps extends Omit<HTMLAttributes<HTMLSpanElement>, "onChange"> {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  icon?: ReactNode;
}

/** Umschaltbares Etikett (Ersatz für Antd `Tag.CheckableTag`). */
export function CheckableTag({ checked, onChange, icon, className, children, ...rest }: CheckableTagProps) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      className={cx("ck-tag ck-tag--checkable", checked && "is-checked", className)}
      data-tone="neutral"
      onClick={() => onChange?.(!checked)}
      onKeyDown={(event) => {
        if (event.key === KEY_SPACE || event.key === KEY_ENTER) {
          event.preventDefault();
          onChange?.(!checked);
        }
      }}
      {...rest}
    >
      {icon ? <span className="ck-tag-icon" aria-hidden="true">{icon}</span> : null}
      {children}
    </span>
  );
}

